import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ChevronRight, Download, Home, MapPin, RotateCcw, TrendingUp } from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useFacilityTypes } from '../../hooks/useFacilities';
import { analyticsApi } from '../../lib/api-client';
import { cn } from '../../lib/utils';
import SingleLocationSelector from '../SingleLocationSelector';
import PieChart from '../PieChart';
import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Input } from '../ui/Input';
import type { DecisionsAnalyticsPageDto } from '../../types/api';

// ─── Decision color/label maps ────────────────────────────────────────────────

const DECISION_COLORS: Record<string, string> = {
  COMPLIANT: '#10b981',
  NON_COMPLIANT: '#ef4444',
  NO_ACTION: '#94a3b8',
  WARNING: '#f59e0b',
  FINES: '#f97316',
  CLOSURE: '#dc2626',
  TEMPORARY_CLOSING: '#b91c1c',
  PERMANENT_CLOSING_OF_THE_FACILITY: '#7f1d1d',
  RELOCATION_OF_THE_HOUSEHOLD: '#8b5cf6',
};

const DECISION_LABELS: Record<string, string> = {
  COMPLIANT: 'Compliant',
  NON_COMPLIANT: 'Non-Compliant',
  NO_ACTION: 'No Action',
  WARNING: 'Warning',
  FINES: 'Fines',
  CLOSURE: 'Closure',
  TEMPORARY_CLOSING: 'Temporary Closing',
  PERMANENT_CLOSING_OF_THE_FACILITY: 'Permanent Closing',
  RELOCATION_OF_THE_HOUSEHOLD: 'Relocation',
};

const DEFAULT_COLOR = '#94a3b8';

function getDecisionColor(decision: string): string {
  return DECISION_COLORS[decision] ?? DEFAULT_COLOR;
}

function getDecisionLabel(decision: string): string {
  return DECISION_LABELS[decision] ?? decision.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

function formatRwf(amount: number): string {
  return `RWF ${amount.toLocaleString()}`;
}

function formatRwfAbbrev(amount: number): string {
  if (amount >= 1_000_000) return `${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `${(amount / 1_000).toFixed(0)}K`;
  return String(amount);
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface DecisionsPanelProps {
  assessmentType: 'HYGIENE' | 'COMPLIANCE';
}

interface BreadcrumbItem {
  id: string;
  name: string;
  locationType: string;
}

type Granularity = 'monthly' | 'weekly';

// ─── Metric card ─────────────────────────────────────────────────────────────

function MetricCard({
  title,
  value,
  subtitle,
  iconBg,
  iconColor,
  icon: Icon,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ElementType;
  iconColor: string;
  iconBg: string;
}) {
  return (
    <Card>
      <CardContent className='p-5'>
        <div className='flex items-start justify-between'>
          <div>
            <p className='text-sm font-medium text-muted-foreground mb-1'>{title}</p>
            <p className='text-2xl font-bold text-foreground'>{value}</p>
            {subtitle && <p className='text-xs text-muted-foreground mt-1'>{subtitle}</p>}
          </div>
          <div className={cn('p-2.5 rounded-lg', iconBg)}>
            <Icon className={cn('h-5 w-5', iconColor)} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Custom tooltip for Fines trend chart ─────────────────────────────────────

function FinesTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className='bg-white border border-border rounded-lg px-3 py-2 shadow-md text-sm'>
      <p className='font-semibold text-foreground mb-1'>{d.label ?? label}</p>
      <p className='text-muted-foreground'>Count: <span className='font-medium text-foreground'>{d.count}</span></p>
      <p className='text-orange-600'>Total: <span className='font-medium'>{formatRwf(d.totalFines)}</span></p>
    </div>
  );
}

// ─── Main Panel ───────────────────────────────────────────────────────────────

export default function DecisionsPanel({ assessmentType }: DecisionsPanelProps) {
  const [facilityTypeId, setFacilityTypeId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [locationStack, setLocationStack] = useState<BreadcrumbItem[]>([]);
  const [directLocationId, setDirectLocationId] = useState<string | undefined>(undefined);
  const [locationSelectorKey, setLocationSelectorKey] = useState(0);
  const [granularity, setGranularity] = useState<Granularity>('monthly');
  const [isExporting, setIsExporting] = useState(false);

  const { data: facilityTypes } = useFacilityTypes();

  // locationStack (drill-down via table) takes priority over direct selector
  const currentParentLocationId =
    locationStack.length > 0 ? locationStack[locationStack.length - 1].id : directLocationId;

  const queryParams = {
    assessmentType,
    ...(facilityTypeId ? { facilityTypeId } : {}),
    ...(startDate ? { startDate } : {}),
    ...(endDate ? { endDate } : {}),
    ...(currentParentLocationId ? { parentLocationId: currentParentLocationId } : {}),
  };

  const { data, isLoading, isError, error } = useQuery<DecisionsAnalyticsPageDto>({
    queryKey: ['decisions-panel', assessmentType, facilityTypeId, startDate, endDate, currentParentLocationId],
    queryFn: () => analyticsApi.getDecisionsAnalytics(queryParams),
  });

  const { data: trendData, isLoading: trendLoading } = useQuery({
    queryKey: ['fines-trend', assessmentType, facilityTypeId, startDate, endDate, currentParentLocationId, granularity],
    queryFn: () =>
      analyticsApi.finesTrend({
        assessmentType,
        ...(facilityTypeId ? { facilityTypeId } : {}),
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
        ...(currentParentLocationId ? { parentLocationId: currentParentLocationId } : {}),
        granularity,
      }),
  });

  const hasActiveFilters = !!(facilityTypeId || startDate || endDate || locationStack.length > 0 || directLocationId);

  function handleReset() {
    setFacilityTypeId('');
    setStartDate('');
    setEndDate('');
    setLocationStack([]);
    setDirectLocationId(undefined);
    setLocationSelectorKey(k => k + 1); // remount selector to clear its internal state
  }

  function handleDrillDown(locationId: string, locationName: string, locationType: string) {
    setLocationStack(prev => [...prev, { id: locationId, name: locationName, locationType }]);
    // When drilling down via table, clear the direct location selector state
    setDirectLocationId(undefined);
    setLocationSelectorKey(k => k + 1);
  }

  function handleBreadcrumbClick(index: number) {
    if (index === -1) {
      setLocationStack([]);
    } else {
      setLocationStack(prev => prev.slice(0, index + 1));
    }
  }

  async function handleExport() {
    setIsExporting(true);
    try {
      const blob = await analyticsApi.exportDecisionsAnalytics(queryParams);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `decisions-analytics-${assessmentType.toLowerCase()}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }

  // Derived metrics
  const enforcementCount =
    data?.decisions
      .filter(d => d.decision !== 'COMPLIANT' && d.decision !== 'NO_ACTION')
      .reduce((sum, d) => sum + d.count, 0) ?? 0;
  const totalFines = data?.totalFines ?? 0;

  // Pie chart data
  const pieData =
    data?.decisions.map(d => ({
      name: getDecisionLabel(d.decision),
      value: d.count,
      color: getDecisionColor(d.decision),
      original: d.decision,
    })) ?? [];

  // Trend chart data
  const trendChartData = (trendData?.periods ?? []).map(p => ({
    period: p.period,
    label: p.label,
    count: p.count,
    totalFines: p.totalFines,
  }));

  return (
    <div className='space-y-6'>
      {/* Header row */}
      <div className='flex items-center justify-between gap-4 flex-wrap'>
        <div />
        <Button onClick={handleExport} loading={isExporting} variant='outline' size='sm' className='gap-2'>
          <Download className='h-4 w-4' />
          Export Excel
        </Button>
      </div>

      {/* Filters */}
      <div className='flex flex-wrap gap-3 items-end'>
        <div className='w-48'>
          <Input
            type='select'
            label='Facility Type'
            value={facilityTypeId}
            options={[
              { value: '', label: 'All facility types' },
              ...(facilityTypes ?? []).map(ft => ({ value: ft.id, label: ft.name })),
            ]}
            onChange={e => setFacilityTypeId(String(e.target.value))}
          />
        </div>
        <div className='w-44'>
          <Input
            type='date'
            label='Start Date'
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
          />
        </div>
        <div className='w-44'>
          <Input
            type='date'
            label='End Date'
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
          />
        </div>
        {/* Location filter — selecting any level narrows the data to that location */}
        <div className='flex flex-col gap-1'>
          <span className='text-sm font-medium text-gray-700'>Location</span>
          <SingleLocationSelector
            key={locationSelectorKey}
            compact
            selectedLocationId={directLocationId}
            onLocationChange={(id) => {
              setDirectLocationId(id || undefined);
              setLocationStack([]); // clear breadcrumb drill-down when using selector
            }}
          />
        </div>
        {hasActiveFilters && (
          <Button variant='ghost' size='sm' onClick={handleReset} className='gap-1.5 mb-0.5'>
            <RotateCcw className='h-3.5 w-3.5' />
            Reset
          </Button>
        )}
      </div>

      {/* Breadcrumb */}
      {locationStack.length > 0 && (
        <div className='flex items-center gap-1.5 text-sm flex-wrap'>
          <button
            onClick={() => handleBreadcrumbClick(-1)}
            className='flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors'
          >
            <Home className='h-3.5 w-3.5' />
            <span>All Locations</span>
          </button>
          {locationStack.map((item, i) => (
            <span key={item.id} className='flex items-center gap-1.5'>
              <ChevronRight className='h-3.5 w-3.5 text-muted-foreground' />
              {i < locationStack.length - 1 ? (
                <button
                  onClick={() => handleBreadcrumbClick(i)}
                  className='text-muted-foreground hover:text-foreground transition-colors'
                >
                  {item.name}
                </button>
              ) : (
                <span className='font-medium text-foreground'>{item.name}</span>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className='flex items-center justify-center py-20'>
          <div className='h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-primary' />
        </div>
      )}

      {/* Error state */}
      {isError && !isLoading && (
        <div className='flex flex-col items-center justify-center py-14 gap-3 text-destructive'>
          <MapPin className='h-8 w-8' />
          <p className='text-sm font-medium'>Failed to load decisions analytics.</p>
          <p className='text-xs text-muted-foreground'>{String((error as any)?.message ?? '')}</p>
        </div>
      )}

      {!isLoading && !isError && data && (
        <>
          {/* Metric cards */}
          <div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
            <MetricCard
              title='Total Facilities Assessed'
              value={(data.totalFacilities ?? 0).toLocaleString()}
              subtitle='unique facilities (latest assessment)'
              icon={MapPin}
              iconColor='text-blue-600'
              iconBg='bg-blue-50'
            />
<MetricCard
              title='Enforcement Actions'
              value={enforcementCount.toLocaleString()}
              subtitle={
                (data.totalFacilities ?? 0) > 0
                  ? `${((enforcementCount / data.totalFacilities) * 100).toFixed(1)}% — warnings, fines, closure`
                  : 'Warnings, fines, closure'
              }
              icon={AlertTriangle}
              iconColor='text-red-600'
              iconBg='bg-red-50'
            />
            <MetricCard
              title='Total Fines Issued'
              value={formatRwf(totalFines)}
              icon={TrendingUp}
              iconColor='text-orange-600'
              iconBg='bg-orange-50'
            />
          </div>

          {/* Empty state */}
          {data.decisions.length === 0 ? (
            <div className='flex flex-col items-center justify-center py-20 gap-3'>
              <MapPin className='h-10 w-10 text-muted-foreground' />
              <p className='text-sm font-medium text-foreground'>No data available</p>
              <p className='text-xs text-muted-foreground'>Try adjusting your filters or selecting a different date range.</p>
            </div>
          ) : (
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
              {/* Left: Donut chart */}
              <Card>
                <CardHeader className='pb-2'>
                  <CardTitle className='text-base'>Decision Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='flex justify-center'>
                    <PieChart
                      title=''
                      labels={pieData.map(d => d.name)}
                      series={pieData.map(d => d.value)}
                      colors={pieData.map(d => d.color)}
                      width={300}
                    />
                  </div>
                  <div className='flex flex-wrap gap-2 mt-3 justify-center'>
                    {pieData.map(d => (
                      <span key={d.original} className='flex items-center gap-1.5 text-xs text-muted-foreground'>
                        <span
                          className='inline-block h-2.5 w-2.5 rounded-full flex-shrink-0'
                          style={{ backgroundColor: d.color }}
                        />
                        {d.name}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Right: Ranked decisions list */}
              <Card className='overflow-hidden'>
                <div className='px-4 py-3 border-b border-border flex items-center justify-between'>
                  <div>
                    <h2 className='text-sm font-semibold text-foreground'>Decisions Ranked</h2>
                    <p className='text-[10px] mt-0.5 text-muted-foreground'>By number of facilities</p>
                  </div>
                </div>
                <div className='px-4 py-2'>
                  <div className='flex items-center gap-3 pb-1 border-b border-border mb-1'>
                    <div className='w-6' />
                    <span className='flex-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground'>Decision</span>
                    <span className='text-[10px] font-semibold uppercase tracking-wider text-muted-foreground w-14 text-right'>Count</span>
                    <span className='text-[10px] font-semibold uppercase tracking-wider text-muted-foreground w-10 text-right'>%</span>
                  </div>
                  {data.decisions
                    .slice()
                    .sort((a, b) => b.count - a.count)
                    .map((d, i) => (
                      <div
                        key={d.decision}
                        className='flex items-center gap-3 py-2.5 border-b border-border last:border-0'
                      >
                        {/* Rank badge */}
                        {i === 0 ? (
                          <span className='text-base leading-none w-6 text-center flex-shrink-0'>🥇</span>
                        ) : i === 1 ? (
                          <span className='text-base leading-none w-6 text-center flex-shrink-0'>🥈</span>
                        ) : i === 2 ? (
                          <span className='text-base leading-none w-6 text-center flex-shrink-0'>🥉</span>
                        ) : (
                          <span className='w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 bg-muted text-muted-foreground'>
                            {i + 1}
                          </span>
                        )}
                        {/* Decision name */}
                        <div className='flex-1 min-w-0'>
                          <div className='flex items-center gap-1.5'>
                            <span
                              className='inline-block h-2 w-2 rounded-full flex-shrink-0'
                              style={{ backgroundColor: getDecisionColor(d.decision) }}
                            />
                            <p className='text-sm font-medium text-foreground truncate'>
                              {getDecisionLabel(d.decision)}
                            </p>
                          </div>
                          {d.decision === 'FINES' && (d as any).totalChargedAmount != null && (
                            <p className='text-[10px] text-orange-500 mt-0.5 pl-3.5'>
                              {formatRwf((d as any).totalChargedAmount)} total fined
                            </p>
                          )}
                        </div>
                        {/* Count */}
                        <span className='text-sm font-bold w-14 text-right flex-shrink-0' style={{ color: getDecisionColor(d.decision) }}>
                          {d.count.toLocaleString()}
                        </span>
                        {/* Percentage */}
                        <span className='text-xs text-muted-foreground w-10 text-right flex-shrink-0'>
                          {d.percentage.toFixed(1)}%
                        </span>
                      </div>
                    ))}
                </div>
              </Card>
            </div>
          )}

          {/* By Location section */}
          {data.byLocation && data.byLocation.length > 0 && (
            <Card>
              <CardHeader className='pb-3'>
                <CardTitle className='text-sm font-semibold text-foreground'>
                  Breakdown by Location
                  {data.childLocationType && (
                    <span className='ml-1.5 text-xs font-normal text-muted-foreground uppercase tracking-wide'>
                      ({data.childLocationType})
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className='p-0'>
                <div className='overflow-x-auto'>
                  <table className='w-full text-sm'>
                    <thead>
                      <tr className='border-b border-border bg-muted/30'>
                        <th className='text-left py-2.5 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wide'>Location</th>
                        <th className='text-right py-2.5 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wide'>Total</th>
                        {(data.byLocation[0]?.byDecision ?? []).map(bd => (
                          <th
                            key={bd.decision}
                            className='text-right py-2.5 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wide whitespace-nowrap'
                          >
                            {getDecisionLabel(bd.decision)}
                          </th>
                        ))}
                        <th className='w-8' />
                      </tr>
                    </thead>
                    <tbody>
                      {data.byLocation.map((loc, i) => (
                        <tr
                          key={loc.locationId}
                          className={cn(
                            'cursor-pointer transition-colors hover:bg-muted/40',
                            i !== data.byLocation.length - 1 && 'border-b border-border/60',
                          )}
                          onClick={() => handleDrillDown(loc.locationId, loc.locationName, loc.locationType)}
                        >
                          <td className='py-3 px-4'>
                            <span className='font-medium text-foreground'>{loc.locationName}</span>
                            <span className='ml-1.5 text-xs text-muted-foreground lowercase'>({loc.locationType})</span>
                          </td>
                          <td className='py-3 px-4 text-right font-bold text-foreground'>{loc.total.toLocaleString()}</td>
                          {loc.byDecision.map(bd => (
                            <td key={bd.decision} className='py-3 px-4 text-right'>
                              {bd.count > 0 && (
                                <span className='font-semibold' style={{ color: getDecisionColor(bd.decision) }}>
                                  {bd.count.toLocaleString()}
                                </span>
                              )}
                            </td>
                          ))}
                          <td className='py-3 px-2 text-muted-foreground'>
                            <ChevronRight className='h-3.5 w-3.5' />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Fines Trend Line Chart */}
          <Card>
            <CardHeader className='pb-2'>
              <div className='flex items-center justify-between gap-4'>
                <CardTitle className='text-base'>Fines Over Time</CardTitle>
                <div className='flex gap-1'>
                  {(['monthly', 'weekly'] as Granularity[]).map(g => (
                    <button
                      key={g}
                      onClick={() => setGranularity(g)}
                      className={cn(
                        'px-3 py-1 text-xs font-medium rounded-md border transition-colors',
                        granularity === g
                          ? 'bg-primary text-white border-primary'
                          : 'bg-white text-muted-foreground border-border hover:text-foreground'
                      )}
                    >
                      {g === 'monthly' ? 'Monthly' : 'Weekly'}
                    </button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {trendLoading ? (
                <div className='flex items-center justify-center py-12'>
                  <div className='h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-orange-500' />
                </div>
              ) : trendChartData.length === 0 ? (
                <div className='flex flex-col items-center justify-center py-12 gap-2'>
                  <p className='text-sm text-muted-foreground'>No fines data available for this period.</p>
                </div>
              ) : (
                <ResponsiveContainer width='100%' height={240}>
                  <LineChart data={trendChartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray='3 3' stroke='#f0f0f0' />
                    <XAxis
                      dataKey='label'
                      tick={{ fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      tickFormatter={v => `RWF ${formatRwfAbbrev(v)}`}
                      tick={{ fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                      width={80}
                    />
                    <Tooltip content={<FinesTooltip />} />
                    <Line
                      type='monotone'
                      dataKey='totalFines'
                      stroke='#f97316'
                      strokeWidth={2}
                      dot={{ fill: '#f97316', r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
